import { useState } from 'react';
import type { Batch, Verb, VerbsData, Word, LearningLanguage } from '../types';
import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import { getBatchLearningLanguage, getLearningLanguageShortLabel, getSourceWordLabel } from '../utils/learningLanguage';
import { buildVerbCardHref, getVerbPracticeConfig } from '../utils/verbPractice';

interface SentenceResultsProps {
  batch: Batch | null;
  currentBatchIndex: number;
  totalBatches: number;
  translating: boolean;
  verbsData?: VerbsData;
  verbsDataByLanguage?: Partial<Record<LearningLanguage, VerbsData>>;
  categoriesMap?: Record<string, Word[]>;
  onTranslate: () => void;
  onNavigate: (direction: number) => void;
  onDelete: () => void;
  generating?: boolean;
  onGenerateSingle?: (verbInf: string, learningLanguage?: LearningLanguage) => void;
  onGenerateForWord?: (word: Word, learningLanguage?: LearningLanguage) => void;
  onNavigateToVerb?: (group: string, verbInf: string, learningLanguage?: LearningLanguage) => void;
  filteredBatchIndices?: number[];
  modeFilter?: string;
  onModeFilterChange?: (mode: string) => void;
  categoryFilter?: string;
  availableCategories?: string[];
  onCategoryFilterChange?: (category: string) => void;
  showModelLabel?: boolean;
  showDebugInfo?: boolean;
  language?: Language;
}

function findVerb(verbsData: VerbsData, inf: string): (Verb & { group: string }) | null {
  for (const group of Object.keys(verbsData)) {
    for (const v of verbsData[group]) {
      if (v.inf === inf) return { ...v, group };
    }
  }
  return null;
}

function findWord(
  categoriesMap: Record<string, Word[]>,
  sourceWord: string,
  learningLanguage: LearningLanguage,
): Word | null {
  for (const cat of Object.keys(categoriesMap)) {
    for (const w of categoriesMap[cat]) {
      if (getSourceWordLabel(w, learningLanguage) === sourceWord) return w;
    }
  }
  return null;
}

export default function SentenceResults({
  batch,
  currentBatchIndex,
  totalBatches,
  translating,
  verbsData: legacyVerbsData,
  verbsDataByLanguage,
  categoriesMap,
  onTranslate,
  onNavigate,
  onDelete,
  generating,
  onGenerateSingle,
  onGenerateForWord,
  onNavigateToVerb,
  filteredBatchIndices,
  modeFilter,
  onModeFilterChange,
  categoryFilter,
  availableCategories,
  onCategoryFilterChange,
  showModelLabel,
  showDebugInfo,
  language = 'en',
}: SentenceResultsProps) {
  const [plVisible, setPlVisible] = useState<Record<number, Record<number, boolean>>>({});
  const [ruVisible, setRuVisible] = useState<Record<number, Record<number, boolean>>>({});
  const [expandedVerb, setExpandedVerb] = useState<string | null>(null);
  const [displayMode, setDisplayMode] = useState<Record<number, 'original' | 'both' | 'translation'>>({});
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  if (!batch && totalBatches === 0) return null;

  const togglePl = (idx: number, checked: boolean) => {
    setPlVisible((prev) => ({
      ...prev,
      [currentBatchIndex]: { ...prev[currentBatchIndex], [idx]: checked },
    }));
  };

  const toggleRu = (idx: number, checked: boolean) => {
    setRuVisible((prev) => ({
      ...prev,
      [currentBatchIndex]: { ...prev[currentBatchIndex], [idx]: checked },
    }));
  };

  const copySentence = async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch {
      /* clipboard not available */
    }
  };

  const copyPrompt = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 1500);
    } catch {
      /* clipboard not available */
    }
  };

  const hasTranslations = batch ? batch.translations && batch.translations.length > 0 : false;
  const batchLearningLanguage = getBatchLearningLanguage(batch);
  const verbConfig = getVerbPracticeConfig(batchLearningLanguage);
  const verbsData = verbsDataByLanguage?.[batchLearningLanguage] || legacyVerbsData || {};
  const sourceLanguageLabel = getLearningLanguageShortLabel(batchLearningLanguage);
  const currentMode = displayMode[currentBatchIndex] || 'both';
  const isOriginalVisible = currentMode === 'original' || currentMode === 'both';
  const isTranslationVisible = currentMode === 'translation' || currentMode === 'both';

  const handleDisplayModeChange = (mode: 'original' | 'both' | 'translation') => {
    setDisplayMode((prev) => ({ ...prev, [currentBatchIndex]: mode }));
    setPlVisible((prev) => ({ ...prev, [currentBatchIndex]: {} }));
    setRuVisible((prev) => ({ ...prev, [currentBatchIndex]: {} }));
  };

  // Filtered navigation
  const filteredPos = filteredBatchIndices
    ? filteredBatchIndices.indexOf(currentBatchIndex)
    : currentBatchIndex;
  const filteredTotal = filteredBatchIndices ? filteredBatchIndices.length : totalBatches;

  return (
    <section id="results-section">
      {batch && (
        <div className="section-header">
          <h2>{t(language, 'section.sentences')}</h2>
          <div className="section-actions">
            {hasTranslations && (
              <div className="display-switcher" id="display-mode-switcher">
                {(['original', 'both', 'translation'] as const).map((mode) => (
                  <button
                    key={mode}
                    className={currentMode === mode ? 'active' : ''}
                    onClick={() => handleDisplayModeChange(mode)}
                  >
                    {t(language, `display.${mode}`)}
                  </button>
                ))}
              </div>
            )}
            {!hasTranslations && (
              <button
                id="translate-btn"
                className="btn btn-secondary"
                onClick={onTranslate}
                disabled={translating}
              >
                {t(language, 'btn.translate')}
              </button>
            )}
          </div>
        </div>
      )}

      {totalBatches > 0 && (
        <div id="batch-nav" className="batch-nav">
          {onModeFilterChange && (
            <select
              className="batch-filter"
              value={modeFilter || 'all'}
              onChange={(e) => onModeFilterChange(e.target.value)}
              aria-label="Filter by type"
            >
              <option value="all">{t(language, 'filter.all')}</option>
              <option value="main">{t(language, 'filter.main')}</option>
              <option value="K">{t(language, 'filter.K')}</option>
              <option value="V">{t(language, 'filter.V')}</option>
              <option value="W">{t(language, 'filter.W')}</option>
            </select>
          )}
          {onCategoryFilterChange && availableCategories && availableCategories.length > 0 && (modeFilter === 'main' || modeFilter === 'W') && (
            <select
              className="batch-filter"
              value={categoryFilter || 'all'}
              onChange={(e) => onCategoryFilterChange(e.target.value)}
              aria-label="Filter by category"
            >
              <option value="all">{t(language, 'filter.allCategories')}</option>
              {availableCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
          <button
            id="batch-prev"
            className="btn btn-sm btn-outline"
            aria-label="Previous batch"
            disabled={filteredPos <= 0}
            onClick={() => onNavigate(-1)}
          >
            {t(language, 'btn.prev')}
          </button>
          <span id="batch-indicator" className="batch-indicator">
            {filteredTotal > 0 ? `${filteredPos + 1} / ${filteredTotal}` : '0 / 0'}
          </span>
          <button
            id="batch-next"
            className="btn btn-sm btn-outline"
            aria-label="Next batch"
            disabled={filteredPos >= filteredTotal - 1}
            onClick={() => onNavigate(1)}
          >
            {t(language, 'btn.next')}
          </button>
          {batch && (
            <button
              id="batch-delete"
              className="btn btn-sm btn-outline btn-danger-outline"
              title={t(language, 'btn.delete')}
              aria-label="Delete batch"
              onClick={onDelete}
            >
              🗑
            </button>
          )}
        </div>
      )}

      {batch?.topic && (
        <div className="batch-meta">
          <span className="batch-meta-pill">{t(language, 'topic.label')}: {batch.topic}</span>
        </div>
      )}

      {batch && <div id="used-words" className="used-words">
        {batch.usedWords.map((w, i) => {
          const isVerbMode = (batch.mode.startsWith('K') || batch.mode.startsWith('V')) && Object.keys(verbsData).length > 0;
          const isMainMode = batch.mode === 'main' || batch.mode === 'W';
          const verb = isVerbMode ? findVerb(verbsData, w) : null;
          const word = isMainMode && categoriesMap ? findWord(categoriesMap, w, batchLearningLanguage) : null;
          const isExpanded = expandedVerb === w;
          return (
            <span key={i}>
              <span
                className={`used-word-tag${verb ? ' used-word-tag-verb' : ''}${word ? ' used-word-tag-word' : ''}`}
                onClick={(verb || word) ? () => setExpandedVerb(isExpanded ? null : w) : undefined}
              >
                {w}
              </span>
              {verb && isExpanded && (
                <span className="verb-info-popup">
                  <span className="verb-info-header">
                    {onNavigateToVerb && (batch.mode === 'V' || batch.mode.startsWith('K')) ? (
                      <a
                        className="verb-info-translation verb-info-link"
                        href={buildVerbCardHref(verb.group, verb.inf, batchLearningLanguage)}
                        aria-label={`Open ${verb.inf} conjugation card`}
                        onClick={(e) => {
                          e.preventDefault();
                          onNavigateToVerb(verb.group, verb.inf, batchLearningLanguage);
                        }}
                      >
                        {verb.inf} — {verb.ru} →
                      </a>
                    ) : (
                      <span className="verb-info-translation">{verb.inf} — {verb.ru}</span>
                    )}
                    {onGenerateSingle && (
                      <button
                        className="btn btn-primary btn-sm btn-generate-single"
                        disabled={generating}
                        onClick={() => onGenerateSingle(verb.inf, batchLearningLanguage)}
                        title={t(language, 'word.generateForVerb')}
                      >
                        ✨
                      </button>
                    )}
                  </span>
                  <span className="verb-info-forms">
                    {verbConfig.pronounOrder.map((p) =>
                      verb.forms[p] ? (
                        <span key={p} className="verb-info-form">
                          <span className="verb-info-pronoun">{p}</span> {verb.forms[p]}
                        </span>
                      ) : null,
                    )}
                  </span>
                </span>
              )}
              {word && isExpanded && (
                <span className="word-info-popup">
                  <span className="word-info-header">
                    <span className="word-info-detail">{getSourceWordLabel(word, batchLearningLanguage)} — {word.ru}</span>
                    {onGenerateForWord && (
                      <button
                        className="btn btn-primary btn-sm btn-generate-single"
                        disabled={generating}
                        onClick={() => onGenerateForWord(word, batchLearningLanguage)}
                        title={t(language, 'word.generateForWord')}
                      >
                        ✨
                      </button>
                    )}
                  </span>
                  <span className="word-info-meta">
                    {word.pos}{word.gender ? ` · ${word.gender}` : ''}{word.level ? ` · ${word.level}` : ''}
                  </span>
                </span>
              )}
            </span>
          );
        })}
      </div>}

      {batch?.llmRequest && showModelLabel && (
        <div className="model-label">
          🤖 {batch.llmRequest.model} <span className="model-label-provider">({batch.llmRequest.provider})</span>
        </div>
      )}

      {batch?.llmRequest && showDebugInfo && (
        <details className="generate-debug">
          <summary>🔍 Debug Info</summary>
          <div className="chat-debug-content">
            <div className="chat-debug-row">
              <span className="chat-debug-label">Provider:</span>
              <span className="chat-debug-value">{batch.llmRequest.provider}</span>
            </div>
            <div className="chat-debug-row">
              <span className="chat-debug-label">Model:</span>
              <span className="chat-debug-value">{batch.llmRequest.model}</span>
            </div>
            <div className="chat-debug-row">
              <span className="chat-debug-label">Max Tokens:</span>
              <span className="chat-debug-value">{batch.llmRequest.maxTokens}</span>
            </div>
            <div className="chat-debug-row">
              <span className="chat-debug-label">Temperature:</span>
              <span className="chat-debug-value">{batch.llmRequest.temperature}</span>
            </div>
            {batch.llmRequest.topP !== undefined && (
              <div className="chat-debug-row">
                <span className="chat-debug-label">Top P:</span>
                <span className="chat-debug-value">{batch.llmRequest.topP}</span>
              </div>
            )}
            {batch.llmRequest.topK !== undefined && (
              <div className="chat-debug-row">
                <span className="chat-debug-label">Top K:</span>
                <span className="chat-debug-value">{batch.llmRequest.topK}</span>
              </div>
            )}
            {batch.llmRequest.thinkingBudget !== undefined && (
              <div className="chat-debug-row">
                <span className="chat-debug-label">Thinking:</span>
                <span className="chat-debug-value">{batch.llmRequest.thinkingBudget} tokens</span>
              </div>
            )}
            {batch.llmRequest.thinkingLevel !== undefined && (
              <div className="chat-debug-row">
                <span className="chat-debug-label">Thinking:</span>
                <span className="chat-debug-value">{batch.llmRequest.thinkingLevel}</span>
              </div>
            )}
            <div className="chat-debug-row" style={{ marginTop: 8 }}>
              <span className="chat-debug-label">Prompt:</span>
              <button
                className="btn-copy-sentence"
                title="Copy prompt"
                aria-label="Copy prompt"
                onClick={() => copyPrompt(batch.llmRequest!.prompt)}
              >
                {copiedPrompt ? '✓' : '📋'}
              </button>
            </div>
            <pre>{batch.llmRequest.prompt}</pre>
            {batch.tokenUsage && (
              <>
                <div className="chat-debug-row" style={{ marginTop: 12 }}>
                  <span className="chat-debug-label">Input Tokens:</span>
                  <span className="chat-debug-value">{batch.tokenUsage.inputTokens}</span>
                </div>
                <div className="chat-debug-row">
                  <span className="chat-debug-label">Output Tokens:</span>
                  <span className="chat-debug-value">{batch.tokenUsage.outputTokens}</span>
                </div>
                {batch.tokenUsage.thinkingTokens !== undefined && batch.tokenUsage.thinkingTokens > 0 && (
                  <div className="chat-debug-row">
                    <span className="chat-debug-label">Thinking:</span>
                    <span className="chat-debug-value">{batch.tokenUsage.thinkingTokens} tokens</span>
                  </div>
                )}
                <div className="chat-debug-row">
                  <span className="chat-debug-label">Total Tokens:</span>
                  <span className="chat-debug-value">{batch.tokenUsage.totalTokens}</span>
                </div>
                {batch.tokenUsage.estimatedCost && (
                  <div className="chat-debug-row">
                    <span className="chat-debug-label">Est. Cost:</span>
                    <span className="chat-debug-value">
                      ${batch.tokenUsage.estimatedCost.totalCost.toFixed(6)} {batch.tokenUsage.estimatedCost.currency}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </details>
      )}

      {batch && (
      <ol id="sentences-list" className="sentences-list">
        {batch.sentences.map((s, i) => {
          const batchPl = plVisible[currentBatchIndex] || {};
          const batchRu = ruVisible[currentBatchIndex] || {};
          const hasPlOverride = i in batchPl;
          const hasRuOverride = i in batchRu;
          const isPlShown = hasPlOverride ? batchPl[i] : isOriginalVisible;
          const isRuShown = hasRuOverride ? batchRu[i] : isTranslationVisible;
          const modeLabel = batch.mode !== 'main' ? batch.mode : null;
          return (
            <li key={i} className="sentence-item">
              {modeLabel && <span className="sentence-mode-label">{modeLabel}</span>}
              <span className={`sentence-pl${!isPlShown ? ' hidden' : ''}`}>{s}</span>
              <button
                className="btn-copy-sentence"
                title={t(language, 'btn.copy')}
                aria-label="Copy sentence"
                onClick={() => copySentence(s, i)}
              >
                {copiedIdx === i ? '✓' : '📋'}
              </button>
              {hasTranslations && batch.translations![i] && (
                <>
                  <span className={`sentence-ru${!isRuShown ? ' hidden' : ''}`}>{batch.translations![i]}</span>
                  <div className="sentence-toggles">
                    <label className="pl-toggle">
                      <input
                        type="checkbox"
                        checked={isPlShown}
                        onChange={(e) => togglePl(i, e.target.checked)}
                      />{' '}
                      {sourceLanguageLabel}
                    </label>
                    <label className="ru-toggle">
                      <input
                        type="checkbox"
                        checked={isRuShown}
                        onChange={(e) => toggleRu(i, e.target.checked)}
                      />{' '}
                      RU
                    </label>
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ol>
      )}

      {translating && (
        <div id="translations-loading" className="loading">
          <div className="spinner"></div>
          <span>{t(language, 'loading.translating')}</span>
        </div>
      )}
    </section>
  );
}
